// src/components/InquiryPopup.jsx
import React, { useState, useEffect } from 'react';
import {
  FiX,
  FiUser,
  FiMail,
  FiBriefcase,
  FiMessageCircle,
  FiSend,
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiDollarSign,
  FiStar,
  FiArrowRight,
  FiPackage,
  FiImage,
  FiCheck,
  FiHeart,
  FiShield,
  FiZap,
  FiChevronDown,
  FiChevronUp,
  FiGrid,
  FiList,
  FiAward,
  FiTrendingUp,
  FiInfo,
} from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';
import { readyMadeService } from '../services/readyMadeService';
import { hireService } from '../services/hireService';
import { EmailValidationInput } from '../utils/emailValidation.jsx';

function InquiryPopup({ isOpen, onClose, packageData, type = 'ready-made' }) {
  const { actualTheme } = useTheme();
  const isDark = actualTheme === 'dark';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [showEmailTooltip, setShowEmailTooltip] = useState(false);

  // Email validation state
  const [emailValidation, setEmailValidation] = useState({
    isValid: false,
    errorMessage: null,
    suggestion: null,
    qualityScore: 0,
    isValidating: false,
  });

  // Track touched fields for validation
  const [touchedFields, setTouchedFields] = useState({
    name: false,
    email: false,
    message: false,
  });

  // Reset form when popup opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({ name: '', email: '', company: '', message: '' });
      setSubmitStatus(null);
      setErrorMessage('');
      setImageLoaded(false);
      setShowAllFeatures(false);
      setTouchedFields({ name: false, email: false, message: false });
      setEmailValidation({
        isValid: false,
        errorMessage: null,
        suggestion: null,
        qualityScore: 0,
        isValidating: false,
      });
      setShowEmailTooltip(false);
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleKeyPress = e => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errorMessage) setErrorMessage('');
    if (submitStatus === 'error') setSubmitStatus(null);
  };

  const handleBlur = fieldName => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
  };

  const validateForm = () => {
    // Name validation
    if (!formData.name.trim()) {
      setErrorMessage('Please enter your name');
      return false;
    }

    // Email validation (using our custom validator)
    if (!emailValidation.isValid) {
      setErrorMessage(
        emailValidation.errorMessage || 'Please enter a valid email address',
      );
      return false;
    }

    // Message validation
    if (!formData.message.trim()) {
      setErrorMessage('Please tell us about your project or requirements');
      return false;
    }

    // Message length validation
    if (formData.message.trim().length < 20) {
      setErrorMessage('Please provide more details (minimum 20 characters)');
      return false;
    }

    return true;
  };

  // Handle email validation from the EmailValidationInput
  const handleEmailValidationChange = (isValid, validationResult) => {
    setEmailValidation({
      isValid: isValid,
      errorMessage: validationResult?.reason || null,
      suggestion: validationResult?.suggestions || null,
      qualityScore: validationResult?.score || 0,
      isValidating: false,
    });

    // Clear general error message if email becomes valid
    if (isValid && errorMessage && errorMessage.includes('email')) {
      setErrorMessage('');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Mark all fields as touched
    setTouchedFields({
      name: true,
      email: true,
      message: true,
    });

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      // Ensure packageData has all required fields
      const enrichedPackageData = {
        id: packageData.id,
        name: packageData.name,
        price: packageData.price,
        duration: packageData.duration,
        features: packageData.features || [],
        gradient: packageData.gradient || 'from-purple-500 to-pink-500',
        imageUrl: packageData.imageUrl || null,
        category: packageData.category || 'others',
        popular: packageData.popular || false,
      };

      const inquiryData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        company: formData.company.trim() || '',
        message: formData.message.trim(),
      };

      let result;

      if (type === 'ready-made') {
        // Use readyMadeService for ready-made package inquiries
        result = await readyMadeService.submitInquiry(
          inquiryData,
          enrichedPackageData,
        );
      } else if (type === 'subscription') {
        // For subscription plans
        const subscriptionData = {
          ...inquiryData,
          planName: enrichedPackageData.name,
          billingCycle: packageData.billingCycle || 'monthly',
        };
        result = await hireService.submitSubscriptionInquiry(
          subscriptionData,
          enrichedPackageData,
        );
      } else {
        // Fallback to general hire inquiry
        result = await hireService.submitInquiry(
          inquiryData,
          enrichedPackageData,
        );
      }

      console.log('✅ Inquiry submitted successfully:', result);
      setSubmitStatus('success');

      // Reset form after successful submission
      setTimeout(() => {
        setFormData({ name: '', email: '', company: '', message: '' });
        setSubmitStatus(null);
        onClose();
      }, 2500);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      setSubmitStatus('error');
      setErrorMessage(
        error.message || 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate savings for subscription plans
  const calculateSavings = () => {
    if (type !== 'subscription' || !packageData?.price) return null;
    const monthlyTotal = packageData.price.monthly * 12;
    const savings = monthlyTotal - packageData.price.yearly;
    const savingsPercent = Math.round((savings / monthlyTotal) * 100);
    return { amount: savings, percent: savingsPercent };
  };

  const savings = calculateSavings();
  const features = packageData?.features || [];
  const visibleFeatures = showAllFeatures ? features : features.slice(0, 6);
  const hasMoreFeatures = features.length > 6;

  // Check if form is valid for button enable
  const isFormValid =
    formData.name.trim() &&
    emailValidation.isValid &&
    formData.message.trim() &&
    formData.message.trim().length >= 20;

  if (!isOpen || !packageData) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl animate-fade-in-up"
        style={{ backgroundColor: 'var(--bg)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 flex justify-between items-center p-5 border-b"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'var(--border)' }}
            >
              {type === 'ready-made' ? (
                <FiPackage
                  className="w-5 h-5"
                  style={{ color: 'var(--text)' }}
                />
              ) : (
                <FiTrendingUp
                  className="w-5 h-5"
                  style={{ color: 'var(--text)' }}
                />
              )}
            </div>
            <div>
              <h2
                className="text-xl font-bold"
                style={{ color: 'var(--text)' }}
              >
                {type === 'ready-made'
                  ? 'Package Inquiry'
                  : 'Subscription Inquiry'}
              </h2>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                Complete the form to proceed with your inquiry
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full transition-colors hover:bg-black/10 dark:hover:bg-white/10"
          >
            <FiX className="w-5 h-5" style={{ color: 'var(--text)' }} />
          </button>
        </div>

        {/* Package Details Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {/* Left Side - Package Image & Basic Info */}
          <div
            className="p-5 md:rounded-l-xl overflow-hidden relative"
            style={{ backgroundColor: 'var(--border)' }}
          >
            {/* Package Image - 16:9 Ratio */}
            {packageData.imageUrl ? (
              <div className="relative rounded-xl overflow-hidden mb-4">
                <div className="relative w-full pt-[56.25%]">
                  {!imageLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                      <div
                        className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
                        style={{ borderColor: 'var(--text)' }}
                      />
                    </div>
                  )}
                  <img
                    src={packageData.imageUrl}
                    alt={packageData.name}
                    className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-300 ${
                      imageLoaded ? 'opacity-100' : 'opacity-0'
                    }`}
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageLoaded(true)}
                  />
                </div>
              </div>
            ) : (
              <div className="relative rounded-xl overflow-hidden mb-4 bg-black/5 dark:bg-white/10">
                <div className="relative w-full pt-[56.25%] flex items-center justify-center">
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <FiPackage
                      className="w-12 h-12 mb-2 opacity-30"
                      style={{ color: 'var(--text)' }}
                    />
                    <p
                      className="text-xs opacity-50"
                      style={{ color: 'var(--muted)' }}
                    >
                      No preview available
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Package Name & Price */}
            <div className="mb-4">
              <div className="flex items-start justify-between mb-2">
                <h3
                  className="text-xl font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  {packageData.name}
                </h3>
                {packageData.popular && (
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1"
                    style={{
                      backgroundColor: 'var(--text)',
                      color: 'var(--bg)',
                    }}
                  >
                    <FiStar className="w-3 h-3" />
                    Most Popular
                  </span>
                )}
              </div>

              {/* Price Display */}
              <div className="mb-3">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span
                    className="text-3xl font-bold"
                    style={{ color: 'var(--text)' }}
                  >
                    {type === 'ready-made'
                      ? `$${packageData.price}`
                      : `$${packageData.billingCycle === 'monthly' ? packageData.price?.monthly : packageData.price?.yearly}`}
                  </span>
                  {type === 'subscription' && (
                    <span className="text-sm" style={{ color: 'var(--muted)' }}>
                      /
                      {packageData.billingCycle === 'monthly'
                        ? 'month'
                        : 'year'}
                    </span>
                  )}
                  {type === 'ready-made' && packageData.duration && (
                    <span
                      className="text-sm flex items-center gap-1"
                      style={{ color: 'var(--muted)' }}
                    >
                      <FiClock className="w-3 h-3" />
                      {packageData.duration}
                    </span>
                  )}
                </div>

                {/* Savings Display for Yearly Plans */}
                {type === 'subscription' &&
                  packageData.billingCycle === 'yearly' &&
                  savings &&
                  savings.percent > 0 && (
                    <div className="mt-1">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-500">
                        Save ${savings.amount.toLocaleString()} (
                        {savings.percent}% vs monthly)
                      </span>
                    </div>
                  )}
              </div>

              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                {packageData.description}
              </p>
            </div>

            {/* Duration Info for Subscription */}
            {type === 'subscription' && packageData.duration && (
              <div
                className="mb-4 p-3 rounded-lg"
                style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
              >
                <div className="flex items-center gap-2 text-sm">
                  <FiClock className="w-4 h-4 text-blue-500" />
                  <span style={{ color: 'var(--text)' }}>
                    {packageData.billingCycle === 'monthly'
                      ? packageData.duration.monthly
                      : packageData.duration.yearly}
                  </span>
                </div>
              </div>
            )}

            {/* Trust Badges */}
            <div
              className="border-t pt-3 mt-3 flex justify-around"
              style={{ borderColor: 'var(--border-light)' }}
            >
              <div className="text-center">
                <FiHeart
                  className="w-4 h-4 mx-auto mb-1"
                  style={{ color: 'var(--text)' }}
                />
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  Client First
                </p>
              </div>
              <div className="text-center">
                <FiShield
                  className="w-4 h-4 mx-auto mb-1"
                  style={{ color: 'var(--text)' }}
                />
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  Quality Guaranteed
                </p>
              </div>
              <div className="text-center">
                <FiClock
                  className="w-4 h-4 mx-auto mb-1"
                  style={{ color: 'var(--text)' }}
                />
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  On-time Delivery
                </p>
              </div>
            </div>
          </div>

          {/* Right Side - Features & Form */}
          <div className="p-5">
            {/* Features Section - Enhanced with Show All */}
            {features.length > 0 && (
              <div className="mb-5">
                <div className="flex items-center justify-between mb-3">
                  <h4
                    className="text-sm font-semibold flex items-center gap-2"
                    style={{ color: 'var(--text)' }}
                  >
                    <FiCheckCircle className="w-4 h-4 text-green-500" />
                    What's Included ({features.length})
                  </h4>
                  {hasMoreFeatures && (
                    <button
                      onClick={() => setShowAllFeatures(!showAllFeatures)}
                      className="text-xs flex items-center gap-1 transition-colors hover:opacity-70"
                      style={{ color: '#3b82f6' }}
                    >
                      {showAllFeatures ? (
                        <>
                          Show Less <FiChevronUp className="w-3 h-3" />
                        </>
                      ) : (
                        <>
                          View All ({features.length}){' '}
                          <FiChevronDown className="w-3 h-3" />
                        </>
                      )}
                    </button>
                  )}
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto pr-1">
                  {visibleFeatures.map((feature, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-2 text-sm p-2 rounded-lg transition-all hover:bg-black/5 dark:hover:bg-white/5"
                      style={{ color: 'var(--muted)' }}
                    >
                      <FiCheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-500" />
                      <span className="flex-1">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Feature Count Indicator */}
                {hasMoreFeatures && !showAllFeatures && (
                  <button
                    onClick={() => setShowAllFeatures(true)}
                    className="w-full mt-2 text-center text-xs py-1.5 rounded-lg transition-all hover:bg-black/5 dark:hover:bg-white/5"
                    style={{ color: '#3b82f6' }}
                  >
                    + {features.length - 6} more features
                  </button>
                )}
              </div>
            )}

            {/* Next Steps */}
            <div className="mb-4">
              <div
                className="p-3 rounded-xl"
                style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
              >
                <h4
                  className="text-sm font-semibold mb-2 flex items-center gap-2"
                  style={{ color: '#3b82f6' }}
                >
                  <FiArrowRight className="w-4 h-4" />
                  Next Steps
                </h4>
                <ol
                  className="text-xs space-y-1.5"
                  style={{ color: 'var(--text)' }}
                >
                  <li className="flex items-start gap-2">
                    <span className="w-4 h-4 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5">
                      1
                    </span>
                    <span>Fill out your contact information below</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-4 h-4 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5">
                      2
                    </span>
                    <span>We'll respond within 24 hours</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-4 h-4 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5">
                      3
                    </span>
                    <span>Schedule a consultation call</span>
                  </li>
                </ol>
              </div>
            </div>

            {/* Form Fields */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Field */}
              <div className="relative">
                <div
                  className="absolute left-3 top-3.5"
                  style={{
                    color:
                      focusedField === 'name' ? 'var(--text)' : 'var(--muted)',
                  }}
                >
                  <FiUser className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('name')}
                  onBlur={() => {
                    setFocusedField(null);
                    handleBlur('name');
                  }}
                  placeholder="Your full name *"
                  required
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl outline-none transition-all text-sm"
                  style={{
                    backgroundColor: 'transparent',
                    border: `1px solid ${touchedFields.name && !formData.name ? '#ef4444' : 'var(--border)'}`,
                    color: 'var(--text)',
                  }}
                />
                <div className="h-5 mt-0.5">
                  {touchedFields.name && !formData.name && (
                    <div className="text-xs text-red-500">Name is required</div>
                  )}
                </div>
              </div>

              {/* Email Field - Using EmailValidationInput with Tooltip */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <label
                    className="text-sm font-medium"
                    style={{ color: 'var(--text)' }}
                  >
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div
                    className="relative"
                    onMouseEnter={() => setShowEmailTooltip(true)}
                    onMouseLeave={() => setShowEmailTooltip(false)}
                  >
                    <FiInfo
                      className="w-3.5 h-3.5 cursor-help"
                      style={{ color: '#3b82f6' }}
                    />
                    {showEmailTooltip && (
                      <div
                        className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 rounded-lg shadow-lg z-50 animate-fade-in"
                        style={{
                          backgroundColor: 'var(--bg)',
                          border: '1px solid var(--border)',
                          color: 'var(--text)',
                        }}
                      >
                        <div className="text-xs space-y-1">
                          <p className="font-semibold">
                            Why we need your email?
                          </p>
                          <p>• We'll send confirmation of your inquiry</p>
                          <p>• Get updates about your request</p>
                          <p>• Receive our response within 24 hours</p>
                          <div
                            className="mt-1 pt-1 border-t"
                            style={{ borderColor: 'var(--border)' }}
                          >
                            <p className="text-yellow-500 text-[10px]">
                              ⚠️ Invalid emails won't receive confirmation
                            </p>
                          </div>
                        </div>
                        {/* Tooltip arrow */}
                        <div
                          className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 rotate-45"
                          style={{
                            backgroundColor: 'var(--bg)',
                            borderRight: '1px solid var(--border)',
                            borderBottom: '1px solid var(--border)',
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <EmailValidationInput
                  value={formData.email}
                  onChange={e => {
                    setFormData(prev => ({ ...prev, email: e.target.value }));
                    if (errorMessage && errorMessage.includes('email')) {
                      setErrorMessage('');
                    }
                  }}
                  onBlur={() => handleBlur('email')}
                  placeholder="your@email.com"
                  required
                  className="w-full"
                  onValidationChange={handleEmailValidationChange}
                />
                <div className="h-5 mt-0.5">
                  {touchedFields.email && !emailValidation.isValid && (
                    <div className="text-xs text-red-500">
                      {emailValidation.errorMessage ||
                        'Please enter a valid email address'}
                    </div>
                  )}
                  {emailValidation.isValid &&
                    emailValidation.qualityScore < 70 &&
                    touchedFields.email && (
                      <div className="text-xs text-yellow-500">
                        Email looks valid but may be temporary
                      </div>
                    )}
                </div>
              </div>

              {/* Company Field (Optional) */}
              <div className="relative">
                <div
                  className="absolute left-3 top-3.5"
                  style={{
                    color:
                      focusedField === 'company'
                        ? 'var(--text)'
                        : 'var(--muted)',
                  }}
                >
                  <FiBriefcase className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('company')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Company name (optional)"
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl outline-none transition-all text-sm"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                />
                <div className="h-5 mt-0.5" />
              </div>

              {/* Message Field */}
              <div className="relative">
                <div
                  className="absolute left-3 top-3.5"
                  style={{
                    color:
                      focusedField === 'message'
                        ? 'var(--text)'
                        : 'var(--muted)',
                  }}
                >
                  <FiMessageCircle className="w-4 h-4" />
                </div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('message')}
                  onBlur={() => {
                    setFocusedField(null);
                    handleBlur('message');
                  }}
                  placeholder="Tell us more about your project or requirements... *"
                  rows="3"
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl outline-none transition-all resize-none text-sm"
                  style={{
                    backgroundColor: 'transparent',
                    border: `1px solid ${touchedFields.message && (!formData.message || formData.message.length < 20) ? '#ef4444' : 'var(--border)'}`,
                    color: 'var(--text)',
                  }}
                />
                <div className="h-5 mt-0.5">
                  {touchedFields.message && !formData.message && (
                    <div className="text-xs text-red-500">
                      Please tell us about your project
                    </div>
                  )}
                  {touchedFields.message &&
                    formData.message &&
                    formData.message.length < 20 && (
                      <div className="text-xs text-yellow-500">
                        {20 - formData.message.length} more characters needed
                        (minimum 20)
                      </div>
                    )}
                </div>
              </div>

              {/* Error Message Display */}
              {errorMessage && (
                <div
                  className="flex items-center gap-2 p-2.5 rounded-lg animate-fade-in"
                  style={{
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                  }}
                >
                  <FiAlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <span className="text-xs text-red-500">{errorMessage}</span>
                </div>
              )}

              {/* Success Message */}
              {submitStatus === 'success' && (
                <div
                  className="flex items-center gap-2 p-2.5 rounded-lg animate-fade-in"
                  style={{
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                  }}
                >
                  <FiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-xs text-green-500">
                    Inquiry submitted successfully! We'll get back to you soon.
                  </span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || !isFormValid}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all hover:opacity-80 disabled:opacity-50 text-sm"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Confirm Inquiry
                    <FiSend className="w-4 h-4" />
                  </>
                )}
              </button>

              {/* Form Status Summary */}
              <div
                className="flex items-center justify-center gap-4 text-xs"
                style={{ color: 'var(--muted)' }}
              >
                <span className="flex items-center gap-1">
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      formData.name.trim() ? 'bg-green-500' : 'bg-gray-400'
                    }`}
                  />
                  Name
                </span>
                <span className="flex items-center gap-1">
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      emailValidation.isValid ? 'bg-green-500' : 'bg-gray-400'
                    }`}
                  />
                  Email
                </span>
                <span className="flex items-center gap-1">
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      formData.message.trim().length >= 20
                        ? 'bg-green-500'
                        : 'bg-gray-400'
                    }`}
                  />
                  Message
                </span>
              </div>

              {/* Privacy Notice */}
              <p
                className="text-center text-xs"
                style={{ color: 'var(--muted)' }}
              >
                We respect your privacy. Your information is secure and will
                never be shared.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InquiryPopup;
